/**
 * The ball outcome model.
 *
 * One delivery resolves in four steps, in this order:
 *
 *   1. extras   -- wide/no-ball first, since those are not legal deliveries
 *   2. wicket   -- did the batter survive?
 *   3. runs     -- how many off the bat, given they survived
 *   4. mode     -- which dismissal, and who gets credit
 *
 * Each step starts from a measured phase baseline and bends it with the
 * players' ratings via log-odds. Bending rather than replacing is deliberate:
 * with league-average players every multiplier collapses to ~1 and the engine
 * reproduces real IPL by construction, which is what makes the calibration
 * harness able to converge at all.
 */

import { BASELINES, KEEPER_SHARE_OF_CATCHES } from './baselines.generated.ts';
import type { Coefficients } from './coefficients.ts';
import type { BallContext, BallOutcome, DismissalKind, Player, RunOutcome } from './types.ts';
import type { Rng } from './rng.ts';

/**
 * League-average rating. The ratings pipeline centres BAT/BOWL near here, so
 * an average player against an average bowler produces edge ~0 and leaves the
 * baseline untouched.
 */
const LEAGUE_MEAN = 49;

/** Ratings are 1-99; scale to roughly [-1, 1] around the league mean. */
const scale = (rating: number): number => (rating - LEAGUE_MEAN) / 50;

/** Keeps a probability usable. Nothing is ever certain in cricket. */
const clamp = (p: number, lo: number, hi: number): number => (p < lo ? lo : p > hi ? hi : p);

/**
 * Rating adjustments are applied in log-odds space, not as a multiplier on the
 * probability.
 *
 * The naive form -- `p = base * exp(k * edge)` -- is unbounded, and cricket
 * ratings produce large edges. An all-99 batting line-up against an all-22
 * attack reaches edge ~1.5, where `exp(2 * 1.5)` is a 21x multiplier: the six
 * rate saturates and the innings finishes around 595, against a realistic
 * ceiling of roughly 240-300.
 *
 * No coefficient fixes that. Shrinking k enough to tame the extremes leaves
 * ratings barely mattering in ordinary play, which is the opposite failure.
 * Log-odds saturates toward 1 on its own, so a huge edge approaches a sensible
 * ceiling instead of exploding, while small edges behave almost identically to
 * the multiplicative form.
 *
 * With edge, aggression and form all zero this is the identity: sigmoid(logit(p))
 * is p. That is what keeps league-average players reproducing the measured
 * baseline exactly, which the calibration harness depends on.
 */
const logit = (p: number): number => Math.log(p / (1 - p));
const sigmoid = (x: number): number => 1 / (1 + Math.exp(-x));

/** Applies additive log-odds shifts to a base probability. */
const shift = (base: number, ...terms: number[]): number => {
  let x = logit(clamp(base, 1e-6, 1 - 1e-6));
  for (const t of terms) x += t;
  return sigmoid(x);
};

/**
 * Effective aggression for this ball: the slider, plus automatic chase
 * pressure, minus caution for wickets already lost.
 *
 * A team eight down at 12 an over does not slog blindly, and a team cruising
 * does not crawl. This is what stops the slider from being the only input.
 */
export function effectiveAggression(ctx: BallContext, co: Coefficients): number {
  // Start from how sides intrinsically bat in this phase, then add the
  // captain's slider on top.
  let a = ctx.aggression + co.phaseIntent[ctx.phase];

  if (ctx.chase && ctx.chase.ballsRemaining > 0) {
    const requiredRate = (ctx.chase.runsRequired / ctx.chase.ballsRemaining) * 6;
    const par = BASELINES[ctx.phase].observed.runsPerOver;
    // Normalised by par so "behind the rate" means the same thing in the
    // powerplay as at the death.
    a += clamp((requiredRate - par) / par, -1, 2) * co.chasePressure;
  }

  a -= ctx.wicketsLost * co.wicketCaution;
  return clamp(a, -1, 1.5);
}

/** Cap winners get a small edge only in the phase they earned the cap in. */
function capEdge(player: Player, ctx: BallContext, co: Coefficients, forBatting: boolean): number {
  if (ctx.phase !== 'DEATH') return 0;
  const relevant = forBatting ? player.isOrangeCap : player.isPurpleCap;
  return relevant ? co.capSpecialtyEdge : 0;
}

/**
 * Resolves extras. Runs first because a wide or no-ball is not a legal
 * delivery and never reaches the bat.
 *
 * Rates scale inversely with the bowler's skill: a BOWL 95 bowler sprays far
 * fewer than a part-timer. Death-overs rates are markedly higher in the source
 * data (4.54 wides per 100 balls versus 2.86 in the middle overs), and that
 * comes through the phase baseline rather than being modelled separately.
 */
function rollWide(ctx: BallContext, co: Coefficients, rng: Rng): BallOutcome | null {
  const base = BASELINES[ctx.phase].extrasPerLegalBall;
  // Better bowler -> fewer extras. Bowler form applies too: a bad day means
  // less control.
  const skill = Math.exp(-co.extrasSkillSensitivity * scale(ctx.bowler.bowl)) / ctx.bowlerForm;

  if (rng.chance(base.wides * skill)) {
    return { runsOffBat: 0, extra: { kind: 'wide', runs: 1 }, isLegal: false };
  }
  return null;
}

/** Whether this delivery is a no-ball, which the batter can still score off. */
function rollNoBall(ctx: BallContext, co: Coefficients, rng: Rng): boolean {
  const base = BASELINES[ctx.phase].extrasPerLegalBall;
  const skill = Math.exp(-co.extrasSkillSensitivity * scale(ctx.bowler.bowl)) / ctx.bowlerForm;
  return rng.chance(base.noballs * skill);
}

/**
 * Picks the dismissal mode from the phase-aware distribution.
 *
 * The phase mix is not decoration: run-outs are 5.5% of powerplay dismissals
 * and 12.0% at the death, and stumpings peak in the middle overs when batters
 * charge the spinners. Using a flat distribution is one of the fastest ways to
 * make a sim feel fake.
 *
 * A free hit removes every mode except run out and obstructing the field.
 */
function rollDismissal(
  ctx: BallContext,
  protectedBall: boolean,
  rng: Rng,
): { kind: DismissalKind; creditBowler: boolean } {
  const modes = BASELINES[ctx.phase].dismissalModes;
  const kinds = Object.keys(modes) as DismissalKind[];
  const weights = kinds.map((k) => {
    if (protectedBall && k !== 'run out' && k !== 'obstructing the field') return 0;
    return modes[k];
  });

  const kind = kinds[rng.weightedIndex(weights)];
  // Run outs and obstruction are not the bowler's wicket.
  const creditBowler = kind !== 'run out' && kind !== 'obstructing the field';
  return { kind, creditBowler };
}

/**
 * Resolves one delivery.
 *
 * `ctx.strikerForm` and `ctx.bowlerForm` are per-innings multipliers so that a
 * 99-rated player genuinely can have a bad day, as the design requires.
 */
export function resolveBall(ctx: BallContext, co: Coefficients, rng: Rng): BallOutcome {
  // A wide never reaches the bat, so it short-circuits.
  const wide = rollWide(ctx, co, rng);
  if (wide) return wide;

  // A no-ball is different: it carries a penalty run but the batter still faces
  // it and can score off it. Treating it as a dead delivery (as a first pass
  // did) both suppressed the run rate and made free hits meaningless, since the
  // ball that earned the free hit could never itself be hit.
  const isNoBall = rollNoBall(ctx, co, rng);

  // On a free hit or a no-ball the batter cannot be dismissed except by running.
  const protectedBall = ctx.isFreeHit || isNoBall;
  const noBallExtra = isNoBall ? ({ kind: 'noball', runs: 1 } as const) : undefined;

  const base = BASELINES[ctx.phase];
  const aggression = effectiveAggression(ctx, co);

  // The bowler's Purple Cap edge belongs on the survival contest: a proven
  // wicket-taker is harder to get away at the death. Previously only the
  // batting (Orange Cap) side of capEdge was ever called, so the Purple Cap was
  // a badge with no mechanical effect at all.
  const bowlCap = capEdge(ctx.bowler, ctx, co, false);

  const survivalEdge = scale(ctx.striker.bat) - scale(ctx.bowler.bowl) - bowlCap;
  const fourEdge =
    0.6 * scale(ctx.striker.bat) + 0.4 * scale(ctx.striker.pow) - scale(ctx.bowler.bowl) - bowlCap;
  const sixEdge = scale(ctx.striker.pow) - scale(ctx.bowler.bowl) - bowlCap;

  // --- wicket ---------------------------------------------------------------
  // Better batter survives more; aggression and a bowler in form cut both ways.
  // Form arrives as a multiplier around 1, so it enters log-odds as its log.
  let pWicket = shift(
    base.wicketRate,
    -co.kWicket * survivalEdge,
    co.aggressionWicket * aggression,
    Math.log(ctx.bowlerForm) - Math.log(ctx.strikerForm),
  );

  // Protected deliveries can only be lost to a run out, so the wicket rate
  // collapses to the run-out share.
  if (protectedBall) pWicket *= base.dismissalModes['run out'] ?? 0.06;

  pWicket = clamp(pWicket, 0.0005, 0.45);

  if (rng.chance(pWicket)) {
    return {
      runsOffBat: 0,
      wicket: rollDismissal(ctx, protectedBall, rng),
      extra: noBallExtra,
      isLegal: !isNoBall,
    };
  }

  // --- runs off the bat -----------------------------------------------------
  // Boundaries move with the ratings; the remaining probability is renormalised
  // across the non-boundary outcomes, preserving their measured shape.
  const batCap = capEdge(ctx.striker, ctx, co, true);
  const aggTerm = co.aggressionBoundary * aggression;

  const pFour = clamp(
    shift(base.runs[4], co.kFour * (fourEdge + batCap), aggTerm, Math.log(ctx.strikerForm)),
    0.001,
    0.6,
  );
  const pSix = clamp(
    shift(base.runs[6], co.kSix * (sixEdge + batCap), aggTerm, Math.log(ctx.strikerForm)),
    0.0005,
    0.5,
  );

  const boundaryMass = pFour + pSix;
  if (boundaryMass >= 0.95) {
    // Degenerate only for absurd rating gaps; keep the innings well-formed.
    return {
      runsOffBat: rng.chance(pSix / boundaryMass) ? 6 : 4,
      extra: noBallExtra,
      isLegal: !isNoBall,
    };
  }

  const restBase = base.runs[0] + base.runs[1] + base.runs[2] + base.runs[3];
  const restScale = (1 - boundaryMass) / restBase;

  const weights = [
    base.runs[0] * restScale,
    base.runs[1] * restScale,
    base.runs[2] * restScale,
    base.runs[3] * restScale,
    pFour,
    pSix,
  ];
  const outcomes: RunOutcome[] = [0, 1, 2, 3, 4, 6];
  const runsOffBat = outcomes[rng.weightedIndex(weights)];

  // --- byes and legbyes -----------------------------------------------------
  // Only when the bat missed. These add runs to the total but not to the
  // batter, and crucially they mean the ball is not a dot. Leaving them out
  // inflated the measured dot rate by ~2 percentage points and suppressed runs
  // per over across every phase.
  //
  // Note these are LEGAL deliveries -- the batter faces the ball and it counts
  // towards the over. Only wides and no-balls are re-bowled.
  if (runsOffBat === 0 && !isNoBall) {
    const ex = base.extrasPerLegalBall;
    // The published rate is per legal ball; here we are already conditioned on
    // a dot off the bat, so rescale by how often that happens.
    const dotShare = base.runs[0];
    const pLegbye = ex.legbyes / dotShare;
    const pBye = ex.byes / dotShare;

    if (rng.chance(pLegbye)) {
      return { runsOffBat: 0, extra: { kind: 'legbye', runs: byeRuns(rng) }, isLegal: true };
    }
    if (rng.chance(pBye)) {
      return { runsOffBat: 0, extra: { kind: 'bye', runs: byeRuns(rng) }, isLegal: true };
    }
  }

  return { runsOffBat, extra: noBallExtra, isLegal: !isNoBall };
}

/** Byes and legbyes are overwhelmingly singles, occasionally scampered twos. */
function byeRuns(rng: Rng): number {
  const r = rng.float();
  if (r < 0.82) return 1;
  if (r < 0.95) return 2;
  return 4;
}

/**
 * Chooses which fielder is credited with a catch.
 *
 * Cricsheet does not tag wicketkeepers in its IPL fielder records, so the
 * keeper's share cannot be measured from the archive and comes from the
 * accepted T20 figure instead.
 */
export function assignCatcher(
  fieldingXI: readonly Player[],
  bowler: Player,
  rng: Rng,
): Player | null {
  const keeper = fieldingXI.find((p) => p.role === 'WK');
  if (keeper && rng.chance(KEEPER_SHARE_OF_CATCHES)) return keeper;

  const others = fieldingXI.filter((p) => p.id !== bowler.id && p.id !== keeper?.id);
  // Returning the bowler here would label the dismissal "caught" while the
  // catcher is the bowler, which is a different mode entirely. Null tells the
  // caller to reclassify it as caught and bowled.
  return others.length > 0 ? rng.pick(others) : null;
}
